import clsx from "clsx";
import { DialogModalWithSteps } from "components/UI/DialogModals";
import TabPanel from "components/UI/Tabs/TabPanel";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import {
  AdditiveAttachmentStore,
  ContractImportationStore as Store,
} from "store";
import ProcessingLayers from "./ProcessingLayers";
import {
  TabAttachments,
  TabColumns,
  TabParameters,
  TabSelectFile,
} from "./Tabs";

import { makeStyles } from "@material-ui/core";
import { lang } from "i18nLang";

const useStyles = makeStyles({
  dialogFixed: {
    width: "80vw",
    height: "71.4vh",
    maxWidth: 640,
    maxHeight: 548,
  },
  content: {
    paddingTop: 0,
    paddingBottom: 0,
    "&.loading": {
      cursor: "wait",
    },
  },
});

interface IProps {
  open: boolean;
  onClose: () => void;
}

/** Área de importação dos aditivos por Arquivo local
 * @category Components
 */
const AdditivesImportFromFile = observer(({ open, onClose }: IProps) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingFileTab, setLoadingFileTab] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const fieldsValues = Store.fields;
  const isProcessing = Store.processing;
  const fileIsValid = Boolean(Store.file);
  const isFileUploaded = Store.isFileUploaded();

  const fieldsIsValid = !hasErrors && Store.allFieldsAreFilled();
  const steps = [
    {
      title: lang(`importation.modeFile.step1`),
      valid: fileIsValid && !loadingFileTab,
    },
    {
      title: lang(`importation.modeFile.step2`),
      valid: !hasErrors,
    },
    {
      title: lang(`importation.modeFile.step3`),
      valid: fieldsIsValid,
    },
    {
      title: lang(`importation.modeFile.stepAtt`),
      valid: true,
      optional: true,
    },
  ];

  useEffect(() => {
    if (isFileUploaded) {
      setLoading(false);
    }
  }, [isFileUploaded]);

  useEffect(() => {
    if (isProcessing) {
      setActiveStep(0);
    }
  }, [isProcessing]);

  const reset = () => {
    Store.reset();
    setActiveStep(0);
    setLoading(false);
  };

  const handleClose = () => {
    AdditiveAttachmentStore.removeAllUnsavedAdditiveAttachment();
    AdditiveAttachmentStore.reset();
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    setLoading(true);
    Store.setProcessing(true);
    AdditiveAttachmentStore.saveAdditiveAttachments()
      .then((attachmentsIds) =>
        Store.startContractImportation(true, attachmentsIds)
      )
      .catch((error) => Store.setAttachmentError());

    // para não fechar o modal
    return true;
  };

  const handleNextStep = (currentStep: number, nextStep: number) => {
    if (!fileIsValid) return;
    // Ja passou do Passo 2
    if (nextStep > 2 && !fieldsIsValid) return;

    setActiveStep(nextStep);
  };

  const handleChangeErrors = (newHasErrors: boolean) => {
    setHasErrors(newHasErrors);
  };

  const handleDropFile = (files: File[]) => {
    if (files?.length) {
      const file = files[0];
      Store.setFile(file);
      Store.setParameter("tabIndex", 0);
      setLoadingFileTab(true);
      Store.setFileTabs(file).finally(() => setLoadingFileTab(false));
    }
  };

  const handleChangeColumn = (field: string, value: string) => {
    Store.setFields(field, value);
  };

  const handleChangeParamenter = (name: string, value: number) => {
    Store.setParameter(name, value);
  };

  return (
    <DialogModalWithSteps
      title={lang(`importation.additive.title`)}
      open={open}
      activeStep={activeStep}
      classes={{ paper: classes.dialogFixed }}
      classNameContent={clsx(classes.content, { loading: loadingFileTab })}
      steps={steps}
      onClose={handleClose}
      onNext={handleNextStep}
      onFinish={handleConfirm}
      finishedLabel={lang("import")}
      showStepsAndActions={!isProcessing}
    >
      {isProcessing && (
        <ProcessingLayers
          isloading={loading}
          type="additive"
          onCloseByComplete={handleClose}
          onCloseByError={reset}
        />
      )}
      {!isProcessing && (
        <>
          <TabPanel value={activeStep} index={0} justifyContent="center">
            <TabSelectFile
              file={Store.file}
              label={lang("importation.yourAdditives")}
              onDropFile={handleDropFile}
            />
          </TabPanel>
          <TabPanel value={activeStep} index={1} justifyContent="center">
            <TabParameters
              values={Store.parameters}
              fileTabs={Store.fileTabs}
              loadingFileTab={loadingFileTab}
              onChange={handleChangeParamenter}
              onChangeErrors={handleChangeErrors}
            />
          </TabPanel>
          <TabPanel value={activeStep} index={2} justifyContent="center">
            <TabColumns
              fields={Store.getFieldsNames()}
              values={fieldsValues}
              validations={Store.validations}
              onChange={handleChangeColumn}
              onChangeErrors={handleChangeErrors}
            />
          </TabPanel>
          <TabPanel value={activeStep} index={3} justifyContent="center">
            <TabAttachments />
          </TabPanel>
        </>
      )}
    </DialogModalWithSteps>
  );
});

export default AdditivesImportFromFile;
